import { useEffect, useMemo, useState } from "react";
import { KnowledgeArticleService } from "../../Services/KnowledgeArticleService";

export type KnowledgeBaseCreateAccessStatus = "loading" | "allowed" | "blocked";

/**
 * Whether an article can be created for this customer (empty = the user's own customer): an article's media
 * is stored in the knowledge-base GCP project of the customer that owns it, so that customer needs one. The
 * backend answers with the very rule it applies on create — the two cannot drift apart. Fail-open on a
 * transport error, so a blip does not lock "create"; the backend still refuses when it has to.
 *
 * Browsing the module never depends on this, only creating an article does. Pass `enabled = false` where
 * there is nothing to check (no manage permission, editing an existing article): no request is made.
 */
export const useKnowledgeBaseCreateAccess = (customerUuid?: string, enabled = true): KnowledgeBaseCreateAccessStatus => {
    const knowledgeArticleService = useMemo(() => new KnowledgeArticleService(), []);
    const [status, setStatus] = useState<KnowledgeBaseCreateAccessStatus>(enabled ? "loading" : "allowed");

    useEffect(() => {
        if (!enabled) {
            setStatus("allowed");
            return undefined;
        }
        let cancelled = false;
        setStatus("loading");
        knowledgeArticleService
            .fetchCanCreate(customerUuid)
            .then((response) => {
                if (!cancelled) {
                    setStatus(response.data.data === false ? "blocked" : "allowed");
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setStatus("allowed");
                }
            });
        return () => {
            cancelled = true;
        };
    }, [customerUuid, enabled, knowledgeArticleService]);

    return status;
};
